import { useEffect, useState } from "react";
import { Eyebrow, Headline } from "@/components/Editorial";
import { Spinner } from "@/components/Loader";
import { affGet } from "@/lib/affiliateApi";
import { fmtMoney } from "@/lib/format";
import { C } from "@/lib/brand";

const FILTERS = [
  { value: "all", label: "All time" },
  { value: "30", label: "Last 30 days" },
  { value: "90", label: "Last 90 days" },
  { value: "ytd", label: "Year to date" },
];

const STATUSES = [
  { value: "all", label: "All status" },
  { value: "active", label: "Active subscribers" },
  { value: "cancelled", label: "Cancelled" },
  { value: "pending", label: "Pending review" },
];

export default function Conversions() {
  const [filter, setFilter] = useState("all");
  const [status, setStatus] = useState("all");
  const [search, setSearch] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        if (filter !== "all") params.set("range", filter);
        if (status !== "all") params.set("status", status);
        if (search) params.set("q", search);
        const qs = params.toString();
        const res = await affGet(`/affiliate/conversions${qs ? `?${qs}` : ""}`, { auth: true });
        if (!cancelled) {
          setData(res || { rows: [] });
          setError(false);
        }
      } catch {
        if (!cancelled) {
          setData({ rows: [] });
          setError(true);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [filter, status, search]);

  const rows = data?.rows || [];

  return (
    <div data-testid="affiliate-conversions" style={{ maxWidth: 1100, margin: "0 auto", padding: "40px 20px 64px" }}>
      <Eyebrow color={C.peachDeep}>★ CONVERSIONS</Eyebrow>
      <Headline before="EVERY" italic="member you've sent." size={48} className="mt-4" />

      <div
        style={{ marginTop: 32, display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 12, alignItems: "end" }}
        className="!grid-cols-1 md:!grid-cols-3"
      >
        <FilterSelect label="DATE RANGE" value={filter} onChange={setFilter} options={FILTERS} testId="conv-filter-range" />
        <FilterSelect label="STATUS" value={status} onChange={setStatus} options={STATUSES} testId="conv-filter-status" />
        <FilterInput label="SEARCH" value={search} onChange={setSearch} placeholder="Email or program" testId="conv-search" />
      </div>

      <div style={{ marginTop: 28, background: C.pure, border: `1px solid ${C.warmGray3}` }}>
        {loading ? (
          <div style={{ padding: 36, display: "flex", justifyContent: "center" }}>
            <Spinner size={28} />
          </div>
        ) : rows.length === 0 ? (
          <div data-testid="conv-empty" style={{ padding: 36, textAlign: "center", color: C.warmGray, fontSize: 14, fontFamily: '"Instrument Serif", Georgia, serif', fontStyle: "italic" }}>
            {error ? "We couldn't load conversions just now. Try again in a minute." : "No conversions yet. Once a member you referred activates a subscription, they'll show up here."}
          </div>
        ) : (
          <>
            <ConversionsHeader />
            {rows.map((row, i) => (
              <ConversionRow
                key={row.id || i}
                row={row}
                expanded={expanded === (row.id || i)}
                onToggle={() => setExpanded(expanded === (row.id || i) ? null : (row.id || i))}
              />
            ))}
          </>
        )}
      </div>
    </div>
  );
}

function FilterSelect({ label, value, onChange, options, testId }) {
  return (
    <label style={{ display: "block" }}>
      <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: "0.2em", color: C.warmGray, marginBottom: 8 }}>
        {label}
      </div>
      <select
        data-testid={testId}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        style={{
          width: "100%",
          padding: "12px 14px",
          background: C.pure,
          border: `1.5px solid ${C.warmGray3}`,
          fontSize: 14,
          color: C.ink,
          appearance: "none",
        }}
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </label>
  );
}

function FilterInput({ label, value, onChange, placeholder, testId }) {
  return (
    <label style={{ display: "block" }}>
      <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: "0.2em", color: C.warmGray, marginBottom: 8 }}>
        {label}
      </div>
      <input
        data-testid={testId}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        style={{
          width: "100%",
          padding: "12px 14px",
          background: C.pure,
          border: `1.5px solid ${C.warmGray3}`,
          fontSize: 14,
          color: C.ink,
        }}
      />
    </label>
  );
}

function ConversionsHeader() {
  return (
    <div
      className="hidden md:grid"
      style={{
        gridTemplateColumns: "1fr 1.4fr 1fr 1fr 0.8fr 28px",
        gap: 12,
        padding: "12px 18px",
        background: C.bone,
        fontSize: 10,
        fontWeight: 800,
        letterSpacing: "0.2em",
        color: C.warmGray,
        borderBottom: `1px solid ${C.warmGray3}`,
      }}
    >
      <div>DATE</div>
      <div>PROGRAM</div>
      <div>TIER</div>
      <div>MRR</div>
      <div>EARNED</div>
      <div />
    </div>
  );
}

function ConversionRow({ row, expanded, onToggle }) {
  return (
    <div data-testid={`conv-row-${row.id || row.date}`} style={{ borderBottom: `1px solid ${C.warmGray3}` }}>
      <button
        type="button"
        onClick={onToggle}
        className="md:!grid-cols-[1fr_1.4fr_1fr_1fr_0.8fr_28px]"
        style={{
          width: "100%",
          background: "transparent",
          border: 0,
          padding: "16px 18px",
          display: "grid",
          gridTemplateColumns: "1fr 28px",
          gap: 12,
          textAlign: "left",
          cursor: "pointer",
          alignItems: "center",
        }}
      >
        <div className="hidden md:block" style={{ fontSize: 13, color: C.warmGray, fontWeight: 700 }}>
          {row.date || "—"}
        </div>
        <div style={{ fontSize: 14, color: C.ink, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis" }}>
          <div style={{ fontWeight: 700 }}>{row.programName || "VOYA9 program"}</div>
          <div className="md:hidden" style={{ fontSize: 12, color: C.warmGray, marginTop: 2 }}>
            {row.date || ""} · {row.tier || ""} · {fmtMoney(row.mrr || 0)}
          </div>
        </div>
        <div className="hidden md:block" style={{ fontSize: 13, color: C.ink }}>{row.tier || "—"}</div>
        <div className="hidden md:block" style={{ fontSize: 13, color: C.ink }}>{fmtMoney(row.mrr || 0)}/mo</div>
        <div className="hidden md:block" style={{ fontSize: 14, color: C.sageDark, fontWeight: 700 }}>
          +{fmtMoney(row.earnedToDate || row.upfrontAmount || 0)}
        </div>
        <div style={{ fontSize: 16, color: C.warmGray, transform: expanded ? "rotate(180deg)" : "rotate(0)", transition: "transform 180ms" }}>
          ↓
        </div>
      </button>
      {expanded && <ConversionDetail row={row} />}
    </div>
  );
}

function ConversionDetail({ row }) {
  const earnings = row.earnings || [];
  return (
    <div data-testid={`conv-detail-${row.id || row.date}`} style={{ background: C.bone, padding: "20px 22px", fontSize: 14, color: C.ink }}>
      <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.2em", color: C.warmGray, marginBottom: 8 }}>
        EARNINGS TIMELINE
      </div>
      <div style={{ marginBottom: 14, fontSize: 13, color: C.warmGray }}>
        Customer signed up {row.signedUpDate || row.date || "—"} · Status:{" "}
        <span style={{ color: row.subscriberStatus === "cancelled" ? C.coral : C.sageDark, fontWeight: 700 }}>
          {row.subscriberStatus || "Active subscriber"}
        </span>
      </div>
      {earnings.length === 0 ? (
        <div style={{ fontSize: 13, color: C.warmGray, fontStyle: "italic", fontFamily: '"Instrument Serif", Georgia, serif' }}>
          Detailed timeline will appear after the first recurring payment.
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {earnings.map((e, i) => (
            <div key={i} style={{ display: "flex", justifyContent: "space-between", padding: "6px 0", borderBottom: `1px dashed ${C.warmGray3}` }}>
              <span style={{ fontSize: 13, color: C.warmGray, fontWeight: 700 }}>{e.date}</span>
              <span style={{ fontSize: 13, flex: 1, padding: "0 14px", color: C.ink }}>{e.label}</span>
              <span style={{ fontSize: 13, color: C.sageDark, fontWeight: 700 }}>+{fmtMoney(e.amount)}</span>
            </div>
          ))}
        </div>
      )}
      <div style={{ marginTop: 16, display: "flex", gap: 24, fontSize: 13 }}>
        <div>
          <div style={{ color: C.warmGray }}>Earned to date</div>
          <div style={{ fontSize: 18, fontWeight: 800, color: C.ink }}>{fmtMoney(row.earnedToDate || 0)}</div>
        </div>
        <div>
          <div style={{ color: C.warmGray }}>Projected total (12 mo)</div>
          <div style={{ fontSize: 18, fontWeight: 800, color: C.ink }}>{fmtMoney(row.projectedTotal || 0)}</div>
        </div>
      </div>
    </div>
  );
}
